import { css, useTheme } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useRef } from 'react';

import type { UseSyncSessionReturn } from '../../hooks/use-sync-session';
import { fontFamilies, fontSizes, spacing } from '../../theme/tokens';
import { Button } from '../../components/ui/button';

export interface ConnectDeviceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  session: UseSyncSessionReturn;
}

export function ConnectDeviceDialog({
  isOpen,
  onClose,
  projectId,
  session,
}: ConnectDeviceDialogProps) {
  const theme = useTheme();
  const autoStartedRef = useRef(false);

  // Auto-start session when dialog opens
  useEffect(() => {
    if (isOpen && session.connectionState === 'idle' && !autoStartedRef.current) {
      autoStartedRef.current = true;
      session.createSession(projectId);
    }
    if (!isOpen) {
      autoStartedRef.current = false;
    }
  }, [isOpen, session, projectId]);

  const followHost = import.meta.env.VITE_FOLLOW_HOST || window.location.origin;
  const joinUrl = session.joinCode ? `${followHost}?code=${session.joinCode}` : '';

  const handleEndSession = useCallback(() => {
    session.leaveSession();
    onClose();
  }, [session, onClose]);

  const handleCopyCode = useCallback(async () => {
    if (joinUrl) {
      await navigator.clipboard.writeText(joinUrl);
    }
  }, [joinUrl]);

  const handleRetry = useCallback(async () => {
    await session.createSession(projectId);
  }, [session, projectId]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          css={css`
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 50;
          `}
        />
        <Dialog.Content
          css={css`
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: ${theme.colors.surface};
            border: 1px solid ${theme.colors.border};
            border-radius: 12px;
            padding: ${spacing.xl};
            width: 90vw;
            max-width: 420px;
            z-index: 51;
            display: flex;
            flex-direction: column;
            gap: ${spacing.lg};
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <Dialog.Title
              css={css`
                margin: 0;
                font-size: ${fontSizes.xl};
                color: ${theme.colors.text};
                font-weight: 700;
              `}
            >
              Connect Device
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close dialog"
              css={css`
                background: none;
                border: none;
                color: ${theme.colors.textSecondary};
                cursor: pointer;
                font-size: 20px;
                min-width: 44px;
                min-height: 44px;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                  color: ${theme.colors.text};
                }
              `}
            >
              ×
            </Dialog.Close>
          </div>

          <Dialog.Description
            css={css`
              margin: 0;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
            `}
          >
            Display your teleprompter on a second device. Scan the QR code or enter the code on your
            phone or tablet to sync.
          </Dialog.Description>

          {session.connectionState === 'connecting' && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: ${spacing.md};
                padding: ${spacing.xl} 0;
              `}
            >
              <div
                role="status"
                aria-label="Creating session"
                css={css`
                  width: 32px;
                  height: 32px;
                  border: 3px solid ${theme.colors.border};
                  border-top-color: ${theme.colors.primary};
                  border-radius: 50%;
                  animation: spin 0.8s linear infinite;

                  @keyframes spin {
                    to {
                      transform: rotate(360deg);
                    }
                  }
                `}
              />
              <p
                css={css`
                  color: ${theme.colors.textSecondary};
                  font-size: ${fontSizes.sm};
                  margin: 0;
                `}
              >
                Creating session...
              </p>
            </div>
          )}

          {session.connectionState === 'idle' && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: ${spacing.md};
                padding: ${spacing.xl} 0;
              `}
            >
              <div
                role="status"
                aria-label="Creating session"
                css={css`
                  width: 32px;
                  height: 32px;
                  border: 3px solid ${theme.colors.border};
                  border-top-color: ${theme.colors.primary};
                  border-radius: 50%;
                  animation: spin 0.8s linear infinite;

                  @keyframes spin {
                    to {
                      transform: rotate(360deg);
                    }
                  }
                `}
              />
              <p
                css={css`
                  color: ${theme.colors.textSecondary};
                  font-size: ${fontSizes.sm};
                  margin: 0;
                `}
              >
                Creating session...
              </p>
            </div>
          )}

          {session.connectionState === 'active' && session.joinCode && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: ${spacing.lg};
              `}
            >
              <div
                css={css`
                  background: white;
                  padding: ${spacing.md};
                  border-radius: 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <QRCodeSVG value={joinUrl} size={180} data-testid="qr-code" />
              </div>

              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: ${spacing.sm};
                `}
              >
                <span
                  data-testid="join-code"
                  css={css`
                    font-family: ${fontFamilies.mono};
                    font-size: ${fontSizes.xxl};
                    font-weight: 700;
                    color: ${theme.colors.text};
                    letter-spacing: 0.15em;
                  `}
                >
                  {session.joinCode}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyCode}
                  aria-label="Copy join code"
                >
                  Copy
                </Button>
              </div>

              <p
                css={css`
                  color: ${theme.colors.textSecondary};
                  font-size: ${fontSizes.sm};
                  margin: 0;
                `}
              >
                {session.participants > 0
                  ? `${session.participants} device${session.participants !== 1 ? 's' : ''} connected`
                  : 'Waiting for device to connect...'}
              </p>

              <Button variant="destructive" onClick={handleEndSession}>
                End Session
              </Button>
            </div>
          )}

          {session.connectionState === 'error' && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: ${spacing.md};
                padding: ${spacing.lg} 0;
              `}
            >
              <p
                role="alert"
                css={css`
                  color: ${theme.colors.error ?? '#ef4444'};
                  font-size: ${fontSizes.sm};
                  margin: 0;
                  text-align: center;
                `}
              >
                {session.error ?? 'An error occurred'}
              </p>
              <Button variant="primary" onClick={handleRetry}>
                Try Again
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
